import { useMemo, useState } from "react";
import { ReconciliationSummaryResponse } from "@common/contracts/reconciliation.contract";
import { cashItemIdsClearedByStatementLines } from "@common/helpers/reconciliationCashItems";
import { FinancialAccount } from "@common/types/financialAccount";
import { useClasses } from "@api/class.api";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import { RECONCILIATION_CLEAR_QUERY_KEY } from "@components/transactions/useTransactionsQuery";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { createReconciliationClearView } from "./createReconciliationClearView";
import { ReconciliationClearStepContent } from "./ReconciliationClearStepContent";

interface Props {
    financialAccount: FinancialAccount;
    summary: ReconciliationSummaryResponse;
    previousClosingDate?: string;
    onBack: () => void;
    onHide: () => void;
    onSuccess?: () => void;
}

export const ReconciliationClearStep: React.FC<Props> = (props) => {
    const { financialAccount, summary } = props;
    const [page, setPage] = useState(1);
    const { enabled: isGlFirst } = useIsGlFirst();
    const hasAnyClass = useClasses().classes.length > 0;
    const entityId = financialAccount.entityId;
    const excludedTransactionIds = useMemo(
        () => cashItemIdsClearedByStatementLines(summary.transactionsPairs),
        [summary.transactionsPairs],
    );

    const defaultView = useMemo(() => {
        if (entityId == null) {
            return null;
        }
        return createReconciliationClearView({
            entityId,
            financialAccountId: financialAccount.id,
            statementsClosingDate: summary.statementsClosingDate,
            hasAnyClass,
            isGlFirst,
            excludedTransactionIds,
        });
    }, [
        entityId,
        financialAccount.id,
        summary.statementsClosingDate,
        hasAnyClass,
        isGlFirst,
        excludedTransactionIds,
    ]);

    if (!defaultView) {
        return null;
    }

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={defaultView}
            paginationMode={{ type: "pages", page, setPage }}
            queryKeyPrefix={RECONCILIATION_CLEAR_QUERY_KEY}
            disableSimilarTransactionsUpdate
            key={excludedTransactionIds.join(",")}
        >
            <ReconciliationClearStepContent
                {...props}
                defaultView={defaultView}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};
